import React, { JSX, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Header.module.scss"

import Epicgames from "@/assets/platforms/epicgames.svg?react";
import Nintendo from "@/assets/platforms/nintendo.svg?react";
import Steam from "@/assets/platforms/steam.svg?react";
import Xbox from "@/assets/platforms/xbox.svg?react";
import PSN from "@/assets/platforms/playstation.svg?react";
import Discord from "@/assets/platforms/discord.svg?react";
import { Platform, PLATFORMS, Profile } from "../../../types";
import Avatar from "../../../components/Avatar/Avatar";
import FollowButton from "../../../components/FollowButton/FollowButton";
import { api } from "../../../services/api";
import EditProfileModal from "../../../components/EditProfileModal/EditProfileModal";
import StatusIndicator from "../../../components/StatusIndicator/StatusIndicator";
import { useOnlineStatus } from "../../../hooks/useOnlineStatus";
import { useUser } from "../../../context/UserContext";

interface HeaderProps {
    profile: Profile,
    onFollowChange?: (isFollowing: boolean) => void
}
const Header = ({ profile, onFollowChange }: HeaderProps) => {
    const { user: contextUser, isMe } = profile;
    const { setUser } = useUser();
    const { fetchStatuses } = useOnlineStatus();
    const accounts = profile.linkedAccounts;
    const [isFollowing, setIsFollowing] = useState(profile.isFollowing ?? false);
    const [hasPendingRequest, setHasPendingRequest] = useState(profile.hasPendingRequest ?? false);
    const [loadingFollowStatus, setLoadingFollowStatus] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [user, setLocalUser] = useState(contextUser);
    const navigate = useNavigate();

    // Keep local user in sync with profile prop
    useEffect(() => {
        setLocalUser(contextUser);
    }, [contextUser]);

    // Sync follow state from profile prop (covers initial load and profile refetch)
    useEffect(() => {
        if (profile.isFollowing !== undefined) setIsFollowing(profile.isFollowing);
        if (profile.hasPendingRequest !== undefined) setHasPendingRequest(profile.hasPendingRequest);
    }, [profile.isFollowing, profile.hasPendingRequest]);

    const handleProfileUpdate = (updatedUser: any) => {
        // Update local state so profile reflects changes immediately
        setLocalUser((prev: any) => ({ ...prev, ...updatedUser }));
        // Update global user context so other components see the change
        setUser((prev) => prev ? { ...prev, ...updatedUser } : prev);
    };

    // Fetch follow status only as fallback when profile didn't include the data
    useEffect(() => {
        const fetchFollowStatus = async () => {
            if (!isMe && user?.userId && profile.isFollowing === undefined) {
                setLoadingFollowStatus(true);
                try {
                    const [followRes, requestRes] = await Promise.all([
                        api.social.isFollowing(user.userId),
                        api.social.getFollowRequestStatus(user.userId),
                    ]);
                    setIsFollowing(followRes.isFollowing);
                    setHasPendingRequest(requestRes.hasPendingRequest);
                } catch (error) {
                    console.error("Failed to fetch follow status:", error);
                } finally {
                    setLoadingFollowStatus(false);
                }
            }
        };

        fetchFollowStatus();
    }, [user?.userId, isMe, profile.isFollowing]);

    // Hydrate online status for the profile user
    useEffect(() => {
        if (user?.userId) {
            fetchStatuses([user.userId]);
        }
    }, [user?.userId, fetchStatuses]);


    const background = user?.profileImageUrl || user?.avatarUrl;

    const handleRedirect = (url: string | undefined) => {
        if (!url) return;
        window.open(url);
    };

    const iconMap: Record<Platform, JSX.Element> = {
        steam: <Steam />,
        playstation: <PSN />,
        nintendo: <Nintendo />,
        xbox: <Xbox />,
        epicgames: <Epicgames/>,
        discord: <Discord/>
    };

    return (

        <section
            className={styles.banner}
            aria-label="User profile header"
        >
            <div className={styles.avatarWrapper}>
                <Avatar src={background} username={user?.username ?? ''} size="10rem" className={styles.avatar} />
                
                <div className={styles.userInfo}>
                    <p className={styles.userName}>{`${user?.username}`}</p>
                    {user?.userId && <StatusIndicator userId={user.userId} variant="full" />}
                    <div className={styles.buttonGroup}>
                        {isMe && <><button id="left" onClick={() => setIsEditModalOpen(true)}>Edit Profile</button>
                            <button id="right">Share Profile</button></>}
                        {!isMe && user?.userId && (
                            <>
                                {!loadingFollowStatus && (
                                    <FollowButton
                                        targetUserId={user.userId}
                                        initialIsFollowing={isFollowing}
                                        isTargetPrivate={profile.isPrivate ?? false}
                                        initialHasPendingRequest={hasPendingRequest}
                                        onFollowChange={(newState) => {
                                            setIsFollowing(newState);
                                            onFollowChange?.(newState);
                                        }}
                                        onRequestChange={(hasPending) => {
                                            setHasPendingRequest(hasPending);
                                        }}
                                    />
                                )}
                                <button
                                    id="right"
                                    onClick={async () => {
                                        try {
                                            const res = await api.messaging.createConversation(user.userId);
                                            navigate(`/messages?conversation=${res.conversationId}`);
                                        } catch (err) {
                                            console.error("Failed to start conversation:", err);
                                            navigate("/messages");
                                        }
                                    }}
                                >
                                    Message
                                </button>
                            </>
                        )}
                    </div>
                    {user?.bio && <p className={styles.bio}>{user.bio}</p>}
                </div>

                <span>
                    <div className={styles.badgeGrid}>
                        {PLATFORMS
                            .filter((p) => accounts?.[p])
                            .map((platform) => (
                                <button
                                    key={platform}
                                    onClick={() => accounts?.[platform]?.profile && handleRedirect(accounts[platform].profile)}
                                    className={styles.iconbutton}
                                    style={{ cursor: accounts?.[platform]?.profile ? 'pointer' : 'default' }}
                                >
                                    {iconMap[platform]}
                                </button>
                            ))}
                    </div>
                </span>
            </div>
            {isMe && (
                <EditProfileModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    userData={{
                        username: user?.username || "",
                        bio: user?.bio || "",
                        avatarUrl: user?.profileImageUrl || user?.avatarUrl,
                    }}
                    onSuccess={handleProfileUpdate}
                />
            )}
        </section>
    );
};

export default Header;
